import React, { PureComponent } from 'react';
import Table, { TableActionBar, TableCell, TableRow } from '@amzn/meridian/table';
import { ContainerDetailsModel, ContainerType } from 'src/components/common/container-details/ContainerDetailsModel';
import Text from '@amzn/meridian/text';
import * as AppStrings from 'src/localization/string-ids/app-strings';
import { getThumbnailFromContainerType } from 'src/data-map/containerDetailsToStringID';
import './ContainerDetails.scss';

interface ContainerDetailsProps {
    containerDetails: ContainerDetailsModel;
    /** Optional test id applied to the results Table (for E2E targeting). */
    tableTestId?: string;
}

type Props = ContainerDetailsProps;

class ContainerDetails extends PureComponent<Props> {

    private getChildContainersTypeFromContainerType = (containerType?: ContainerType): string => {
        switch (containerType) {
            case ContainerType.STACKING_AREA:
            case ContainerType.PALLET:
                return AppStrings.CONTAINER_DETAILS_TOTES;
            case ContainerType.TOTE:
                return AppStrings.CONTAINER_DETAILS_ITEMS;
            default:
                return AppStrings.CONTAINER_DETAILS_NUMBER_OF_CHILD_CONTAINERS;
        }
    }

    private readonly uiStringMap: Record<string, string | ((containerType?: ContainerType) => string)> = {
        cleanDecantDate: AppStrings.CONTAINER_DETAILS_CLEAN_DECANT_DATE,
        latestProcessingDate: AppStrings.CONTAINER_DETAILS_LATEST_PROCESSING_DATE,
        sla: AppStrings.CONTAINER_DETAILS_SLA,
        name: AppStrings.CONTAINER_DETAILS_LATEST_CONTAINER_OPERATION_NAME,
        time: AppStrings.CONTAINER_DETAILS_LATEST_CONTAINER_OPERATION_TIME,
        associate: AppStrings.CONTAINER_DETAILS_LATEST_CONTAINER_OPERATION_ASSOCIATE,
        Asin: AppStrings.CONTAINER_DETAILS_ASIN,
        sellerType: AppStrings.CONTAINER_DETAILS_SELLER_TYPE,
        TTSorTTA: AppStrings.CONTAINER_DETAILS_TTS_OR_TTA,
        cleanDecantSortRuleId: AppStrings.CONTAINER_DETAILS_SORT_RULE_ID,
        groupSortCode: AppStrings.CONTAINER_DETAILS_CATEGORY,
        groupSubSortCode: AppStrings.CONTAINER_DETAILS_SUB_CATEGORY,
        requiredHazmatLabels: AppStrings.CONTAINER_DETAILS_HAZMAT_LABELS,
        numOfChildContainers: (containerType?: ContainerType) => this.getChildContainersTypeFromContainerType(containerType)
    };

    private getUIStrings = (key: string): string => {
        const value = this.uiStringMap[key];
        if (value === undefined) return key;
        return typeof value === 'function' ? value(this.props.containerDetails.containerType) : value;
    }

    private getContainerIdString = (containerType?: ContainerType): string => {
        switch (containerType) {
            case ContainerType.ITEM:
                return AppStrings.CONTAINER_DETAILS_LPN_LABEL;
            case ContainerType.TOTE:
                return AppStrings.CONTAINER_DETAILS_TOTE_ID;
            case ContainerType.PALLET:
                return AppStrings.CONTAINER_DETAILS_PALLET_ID;
            case ContainerType.STACKING_AREA:
                return AppStrings.CONTAINER_DETAILS_STACKING_AREA_ID;
            case ContainerType.DROP_ZONE:
                return AppStrings.CONTAINER_DETAILS_DROP_ZONE_ID;
            case ContainerType.TRAILER:
                return AppStrings.CONTAINER_DETAILS_TRAILER_ID;
            default:
                return AppStrings.CONTAINER_DETAILS_DEFAULT_CONTAINER_ID;
        }
    }

    private getTableActionBar = (containerDetails: ContainerDetailsModel) => {
        return(
            <TableActionBar>
                <Text type="b300">
                    {AppStrings.CONTAINER_DETAILS_ACTION_BAR_RESULT}
                </Text>
                <Text type="h200">{containerDetails.containerId as string}</Text>
            </TableActionBar>
        )
    }

    private getContainerIdRow = () => {
        const containerDetails = this.props.containerDetails;
        return(
            <TableRow>
                <TableCell>
                    <div className="container-div">
                        <img src={getThumbnailFromContainerType(containerDetails.containerType as ContainerType)}
                             alt={''} className="container-thumbnail"/>
                        <Text className="container-id">{this.getContainerIdString(containerDetails?.containerType)}</Text>
                    </div>
                </TableCell>
                <TableCell header={true}>{containerDetails["containerId"]}</TableCell>
                <TableCell />
            </TableRow>
        )
    }

    private getParentContainerRow = () => {
        const containerDetails = this.props.containerDetails;
        return (
            <TableRow>
                <TableCell>{AppStrings.CONTAINER_DETAILS_PARENT_CONTAINER}</TableCell>
                <TableCell header={true}>
                    {containerDetails.parentContainer?.containerType}
                    ({containerDetails.parentContainer?.containerId})
                </TableCell>
                <TableCell/>
            </TableRow>
        )
    }

    private getSortationCategoriesRow = (value: any) => {
        return (
            <TableRow>
                <TableCell>{AppStrings.CONTAINER_DETAILS_SORTATION_CATEGORIES}</TableCell>
                <TableCell header={true}>{value.join(', ')}</TableCell>
            </TableRow>
        )
    }

    private getAdditionalPropertyRow = (key: keyof ContainerDetailsModel, value: unknown) => {
        if (typeof value !== 'object') {
            return (
                <TableRow>
                    <TableCell>{this.getUIStrings(String(key))}</TableCell>
                    <TableCell header={true}>{String(value)}</TableCell>
                </TableRow>
            );
        }

        return Object.entries(value as Record<string, unknown>).map(([subKey, subValue]) => {
            if (!subValue) return null;
            return (
                <TableRow key={`${String(key)}-${subKey}`}>
                    <TableCell>{this.getUIStrings(subKey)}</TableCell>
                    <TableCell header={true}>{String(subValue)}</TableCell>
                </TableRow>
            );
        });
    }

    private renderContainerPropertyRow = (key: keyof ContainerDetailsModel) => {
        const value = this.props.containerDetails[key];
        if (value == null || value === "") return null;

        switch (key) {
            case "containerId":
                return this.getContainerIdRow();
            case "parentContainer":
                return this.getParentContainerRow();
            case "containerType":
                return null;
            case "sortationCategories":
                // Where the sortation codes are vended (Post CD Sorting), Category and Sub Category
                // come from groupSortCode / groupSubSortCode instead, so this row would duplicate
                // the first of them. Other screens keep showing it.
                if (this.props.containerDetails.containerProperties?.groupSortCode) return null;
                return this.getSortationCategoriesRow(value as string[]);
            default:
                return this.getAdditionalPropertyRow(key, value);

        }
    }

    public render() {
        const { containerDetails } = this.props;
        const keys = Object.keys(containerDetails) as Array<keyof typeof containerDetails>;

        return (
            <Table spacing="small" nestedRows={true} className="table-view" data-testid={this.props.tableTestId} >
                {this.getTableActionBar(containerDetails)}
                {keys.map(key => this.renderContainerPropertyRow(key))}
            </Table>
        )
    }
}

export default ContainerDetails;
